import React, { useEffect } from 'react';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { MousePointerClick, Bell, BrainCircuit, LineChart, History, Shield, Brain, LucideComputer, TabletSmartphone, UserCheck2, AlarmClock, Sparkles } from 'lucide-react';

const features = [
  {
    icon: <MousePointerClick size={24} />,
    title: 'One-Click Trading',
    description: 'Execute trades across multiple platforms instantly with a single click, saving time and reducing errors.'
  },
  {
    icon: <Bell size={24} />,
    title: 'Smart Expiry Alerts',
    description: 'Receive timely notifications about expiring options so you never miss critical decision points.'
  },
  {
    icon: <Sparkles size={24} />,
    title: 'AI-Assisted Insights',
    description: 'Surface high-probability trade setups using machine learning.'
  },
  {
    icon: <LineChart size={24} />,
    title: 'Unified Analytics',
    description: 'All your charts, indicators, news, and positions in one place.'
  },
  {
    icon: <AlarmClock size={24} />,
    title: 'Real-Time Sync',
    description: 'Never miss a move. Get notified based on your custom strategy.'
  },
  {
    icon: <UserCheck2 size={24} />,
    title: 'Built for Retail Traders',
    description: 'Lightweight, fast, and designed with usability in mind.'
  }
];

const Features = () => {
  useEffect(() => {
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    });

    // Target elements within the Features component specifically
    const featureSection = document.getElementById('features');
    if (featureSection) {
      const animatedElements = featureSection.querySelectorAll('.animate-on-scroll');
      animatedElements.forEach(el => {
        observer.observe(el);
      });
    }

    return () => {
      if (featureSection) {
        const animatedElements = featureSection.querySelectorAll('.animate-on-scroll');
        animatedElements.forEach(el => observer.unobserve(el));
      }
    };
  }, []);

  return (
    <section className="py-20 relative overflow-hidden" id="features" role="region" aria-labelledby="features-heading">
      <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-up247-50/50 to-transparent -z-10"></div>
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="text-center mb-16">
          <span className="inline-block py-1 px-3 bg-up247-100 text-up247-700 text-xs font-medium rounded-full mb-4">
            Powerful capabilities
          </span>
          <h2 id="features-heading" className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 max-w-2xl mx-auto text-balance">
            Everything you need for <span className="heading-gradient">smarter trading</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto text-balance">
            All the tools you need to make informed trading decisions across multiple platforms.
          </p>
        </div>
        {/* Desktop grid */}
        <div role="list" className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              role="listitem"
              className="animate-on-scroll opacity-0 transition-opacity duration-700 ease-out glass rounded-xl p-6 hover:shadow-lg group"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 rounded-lg bg-up247-100 flex items-center justify-center text-up247-600 mb-5 group-hover:bg-up247-600 group-hover:text-white transition-colors">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
        {/* Mobile carousel */}
        <Carousel className="block md:hidden">
          <CarouselContent className="px-6">
            {features.map((feature, index) => (
              <CarouselItem key={index} role="listitem">
                <div
                  className="animate-on-scroll opacity-0 transition-opacity duration-700 ease-out glass rounded-xl p-6 hover:shadow-lg group"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-12 h-12 rounded-lg bg-up247-100 flex items-center justify-center text-up247-600 mb-5 group-hover:bg-up247-600 group-hover:text-white transition-colors">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </section>
  );
};

export default Features;
